import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { storeSession } from '@/lib/authToken'
import api from '@/lib/api'

/**
 * Sign in. There is one identity provider, so this page does not ask which one — it
 * hands the browser to 42QLabs and gets out of the way.
 *
 * A chooser with a single choice is not a choice, it is an extra click on every sign-in.
 * But "redirect immediately" is wrong in three cases, and each one is a loop or a lockout
 * rather than a cosmetic problem:
 *
 *   - the callback came back with an error. Redirecting would send the browser straight
 *     back to the provider that just failed, and round and round.
 *   - the user just signed out. Their session at 42QLabs is still live, so an immediate
 *     redirect signs them back in and logging out becomes impossible.
 *   - the identity is valid but has no account here (403). The provider will keep saying
 *     yes and Plutus will keep saying no.
 *
 * In those cases the page stops and says why, with a button to try again on purpose.
 */

/** How recently a redirect has to have happened for a token-less return to look like a loop. */
const LOOP_GUARD_MS = 15_000
const LOOP_GUARD_KEY = 'plutus.oidc.redirected_at'

type Phase = 'deciding' | 'redirecting' | 'stopped' | 'unavailable'

export default function Login() {
  const navigate = useNavigate()
  const storeError = useAuthStore((s) => s.error)
  const clearError = useAuthStore((s) => s.clearError)

  const [phase, setPhase] = useState<Phase>('deciding')
  const [notice, setNotice] = useState<string | null>(null)

  const loginUrl = `${import.meta.env.VITE_API_URL || '/api'}/auth/oidc/login`

  useEffect(() => {
    let cancelled = false
    const params = new URLSearchParams(window.location.search)

    // Success: tokens arrive in the fragment so they never reach a server log or the
    // browser history. Consume and clear before anything else looks at the URL.
    const frag = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const token = frag.get('access_token')
    if (token) {
      storeSession(token, Number(frag.get('expires_in') || 3600), frag.get('refresh_token'))
      try { sessionStorage.removeItem(LOOP_GUARD_KEY) } catch { /* private window */ }
      window.history.replaceState({}, '', window.location.pathname)
      clearError()
      // Resolve the local user before navigating: the route guard reads the store, so
      // landing on "/" unresolved would bounce straight back here.
      useAuthStore.getState().checkAuth().then(() => {
        if (!cancelled && useAuthStore.getState().isAuthenticated) navigate('/')
      })
      return
    }

    const authError = params.get('auth_error')
    const signedOut = params.get('signed_out') !== null
    if (authError || signedOut || storeError) {
      if (authError || signedOut) window.history.replaceState({}, '', window.location.pathname)
      setNotice(authError || storeError || null)
      setPhase('stopped')
      return
    }

    // Asked, not assumed: if the provider is not configured or is unreachable, say so
    // instead of redirecting into an Authentik error page.
    api.get('/auth/status')
      .then((r) => {
        if (cancelled) return
        const central = r.data?.central
        if (!central?.configured) {
          setNotice(central?.reason || 'Central identity is not configured on this deployment.')
          setPhase('unavailable')
          return
        }

        let last = 0
        try { last = Number(sessionStorage.getItem(LOOP_GUARD_KEY) || 0) } catch { last = 0 }
        if (last && Date.now() - last < LOOP_GUARD_MS) {
          // Back here with no token and no error, moments after being sent out. Redirecting
          // again would spin the browser, so stop and let the person decide.
          setNotice('The sign-in did not complete. Try again, or contact IT if it keeps happening.')
          setPhase('stopped')
          return
        }

        try { sessionStorage.setItem(LOOP_GUARD_KEY, String(Date.now())) } catch { /* ignore */ }
        setPhase('redirecting')
        // replace, not assign: the login page should not sit in history behind the
        // provider, or Back lands on a page that redirects forward again.
        window.location.replace(loginUrl)
      })
      .catch(() => {
        if (cancelled) return
        setNotice('The Plutus API did not respond. Check that it is running.')
        setPhase('unavailable')
      })

    return () => { cancelled = true }
  }, [navigate, clearError, storeError, loginUrl])

  const retry = () => {
    try { sessionStorage.removeItem(LOOP_GUARD_KEY) } catch { /* ignore */ }
    clearError()
    window.location.href = loginUrl
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm text-center">
        <h1 className="text-3xl font-bold text-primary">Plutus</h1>

        {(phase === 'deciding' || phase === 'redirecting') && (
          <>
            <div
              className="mt-8 mx-auto h-6 w-6 rounded-full border-2 border-primary/25 border-t-primary animate-spin"
              role="status"
              aria-label="Signing in"
            />
            <p className="mt-4 text-sm text-muted-foreground">Taking you to 42QLabs...</p>
          </>
        )}

        {phase === 'stopped' && (
          <div className="mt-8">
            {notice && (
              <p className="mb-6 text-sm text-muted-foreground">{notice}</p>
            )}
            <button
              onClick={retry}
              className="w-full rounded-md bg-primary px-3 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Sign in
            </button>
          </div>
        )}

        {phase === 'unavailable' && (
          <div className="mt-8 rounded-md border border-amber-200 bg-amber-50 p-3 text-left text-sm text-amber-900">
            <p className="font-medium">Sign-in is unavailable on this deployment.</p>
            <p className="mt-1">{notice}</p>
          </div>
        )}
      </div>
    </div>
  )
}
